import { useMemo, useState } from 'react'
import { Input, Empty, Tag, Select, Segmented, Upload, message } from 'antd'
import {
  SearchOutlined,
  WhatsAppOutlined,
  CheckOutlined,
  SyncOutlined,
  QrcodeOutlined,
  PictureOutlined,
  LinkOutlined,
  FontSizeOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { Icon } from '@/components/icons'
import { usePage, router } from '@inertiajs/react'
import EventsLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import { Button } from '@/components/ui'
import {
  type Recipient,
  type RecipientFilters,
  type RecipientFilterOptions,
  defaultRecipientFilters,
} from '../../components/recipient_selection/types'
import {
  RecipientSelectionDrawer,
  type RecipientSelectionDrawerProps,
} from '../../components/recipient_selection/RecipientSelectionDrawer'
import {
  type Event,
  type WhatsappTemplate,
  type WhatsappCampaignFormData,
} from '../types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import { useWizardState } from './useWizardState'
import {
  detectParameterSlots,
  headerTextHasPlaceholder,
  labelForMergeTagValue,
  MAX_HEADER_IMAGE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
  type HeaderImageType,
  type CustomImageMode,
} from './helpers'
import WhatsAppPreview from './WhatsAppPreview'
import { HiOutlineUser, HiOutlineUserGroup } from 'react-icons/hi'
import { api } from '@/lib/apiClient'
import '@/styles/compact-form.css'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const EMPTY_FILTER_OPTIONS: RecipientFilterOptions = { fields: [], sessionChoices: [] }

type PageProps = {
  event: Event
  customTemplates: WhatsappTemplate[]
  templatesError?: string | null
  // These are only provided when template_id is in the URL (step 2):
  template?: WhatsappTemplate
  initialCampaign?: WhatsappCampaignFormData
  availableRecipients?: Recipient[]
  initialRecipientFilters?: RecipientFilters
  filterOptions?: RecipientFilterOptions
  mergeTagOptions?: MergeTagOptions
  errors?: Record<string, string | string[]>
  formMode?: 'create' | 'edit'
}

// ---------------------------------------------------------------------------
// Template Card (compact, for step 1)
// ---------------------------------------------------------------------------

const countVariables = (text?: string): number => {
  if (!text) return 0
  const matches = text.match(/\{\{\d+\}\}/g)
  return matches ? matches.length : 0
}

const getHeaderLabel = (template: WhatsappTemplate): string | null => {
  if (template.hasHeaderImage) return 'Image'
  if (template.hasHeaderText) return 'Text'
  return null
}

function CompactTemplateCard({
  template,
  onSelect,
}: {
  template: WhatsappTemplate
  onSelect: (id: string) => void
}) {
  const status = (template.status ?? 'unknown').toUpperCase()
  const language = template.language ?? 'en_US'
  const variableCount = countVariables(template.bodyText)
  const headerLabel = getHeaderLabel(template)

  const isApproved = status === 'APPROVED'

  // Highlight {{N}} placeholders in body text
  const highlightedBody = (text: string) => {
    const parts = text.split(/(\{\{\d+\}\})/g)
    return parts.map((part, i) =>
      /\{\{\d+\}\}/.test(part) ? (
        <span key={i} className="bg-amber-100 text-amber-700 font-mono text-[10px] px-0.5 rounded">
          {part}
        </span>
      ) : (
        <span key={i}>{part}</span>
      ),
    )
  }

  return (
    <div
      className={`relative bg-white rounded-lg border overflow-hidden transition-all cursor-pointer group ${
        isApproved
          ? 'border-gray-200 hover:border-blue-400 hover:shadow-md'
          : 'border-gray-200 opacity-60'
      }`}
      onClick={() => isApproved && onSelect(template.id)}
    >
      <div className="p-4">
        {/* Header row: name + status */}
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-1 flex-1">
            {template.name}
          </h3>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full flex-shrink-0 ${
              isApproved
                ? 'bg-green-50 text-green-700'
                : status === 'PENDING'
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-red-50 text-red-600'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${
              isApproved
                ? 'bg-green-500'
                : status === 'PENDING'
                  ? 'bg-amber-500'
                  : 'bg-red-500'
            }`} />
            {status}
          </span>
        </div>

        {/* Meta chips */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">
            {language}
          </span>
          {headerLabel && (
            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
              {headerLabel === 'Image' ? (
                <PictureOutlined style={{ fontSize: 10 }} />
              ) : (
                <FontSizeOutlined style={{ fontSize: 10 }} />
              )}
              {headerLabel}
            </span>
          )}
          {variableCount > 0 && (
            <span className="inline-flex items-center text-[11px] text-purple-600 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded-full font-mono">
              {variableCount} var{variableCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Body preview */}
        {template.bodyText ? (
          <div className="bg-gray-50 rounded-md px-3 py-2.5 mb-3 border border-gray-100">
            <p className="text-xs text-gray-600 line-clamp-3 leading-relaxed">
              {highlightedBody(template.bodyText)}
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-md px-3 py-2.5 mb-3 border border-gray-100">
            <p className="text-xs text-gray-400 italic">No message body</p>
          </div>
        )}

        {/* Action */}
        {isApproved ? (
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-gray-400">Click to select</span>
            <span className="text-xs font-medium text-blue-600 group-hover:text-blue-700 transition-colors">
              Use Template &rarr;
            </span>
          </div>
        ) : (
          <div className="text-[11px] text-gray-400 italic">
            Template not yet approved
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------

function CreationStepper({
  currentStep,
  onStepClick,
}: {
  currentStep: 0 | 1
  onStepClick: (step: 0 | 1) => void
}) {
  const steps = [
    { number: 1, label: 'Select Template' },
    { number: 2, label: 'Configure Campaign' },
  ]

  return (
    <div className="flex items-center justify-center gap-0 py-4 px-14">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep
        const isCompleted = idx < currentStep
        const isClickable = isCompleted

        return (
          <div key={idx} className="flex items-center">
            {idx > 0 && (
              <div
                className={`w-24 h-px mx-3 ${
                  isCompleted ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              />
            )}
            <button
              type="button"
              className={`flex items-center gap-2.5 px-3 py-1.5 rounded-full transition-colors ${
                isActive
                  ? 'bg-blue-50 cursor-default'
                  : isClickable
                    ? 'hover:bg-gray-50 cursor-pointer'
                    : 'cursor-default'
              }`}
              onClick={() => isClickable && onStepClick(idx as 0 | 1)}
              disabled={!isClickable && !isActive}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-blue-500 text-white'
                    : isActive
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {isCompleted ? (
                  <CheckOutlined style={{ fontSize: 11 }} />
                ) : (
                  step.number
                )}
              </div>
              <span
                className={`text-sm font-medium ${
                  isActive || isCompleted ? 'text-gray-800' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
            </button>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1: Template Selection
// ---------------------------------------------------------------------------

function TemplateSelectionStep({
  templates,
  eventSlug,
  templatesError,
}: {
  templates: WhatsappTemplate[]
  eventSlug: string
  templatesError?: string | null
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isRefetching, setIsRefetching] = useState(false)

  const handleRefetch = () => {
    setIsRefetching(true)
    window.location.reload()
  }

  const handleOpenMetaDashboard = () => {
    window.open('https://business.facebook.com/', '_blank')
  }

  const metaIcon = <Icon name="meta" />

  // Derive available categories from templates
  const categoryMap = useMemo(() => {
    const map: Record<string, WhatsappTemplate[]> = {}
    templates.forEach((t) => {
      const cat = (t.category ?? 'OTHER').toUpperCase()
      if (!map[cat]) map[cat] = []
      map[cat].push(t)
    })
    return map
  }, [templates])

  // Determine tab order: Marketing first, then Utility, then others
  const tabOrder = useMemo(() => {
    const order: string[] = []
    if (categoryMap['MARKETING']) order.push('MARKETING')
    if (categoryMap['UTILITY']) order.push('UTILITY')
    Object.keys(categoryMap).forEach((cat) => {
      if (!order.includes(cat)) order.push(cat)
    })
    return order
  }, [categoryMap])

  const [activeTab, setActiveTab] = useState(tabOrder[0] || 'MARKETING')

  const filteredTemplates = useMemo(() => {
    const list = categoryMap[activeTab] || []
    if (!searchQuery.trim()) return list
    const query = searchQuery.trim().toLowerCase()
    return list.filter((t) => t.name.toLowerCase().includes(query))
  }, [categoryMap, activeTab, searchQuery])

  const handleSelectTemplate = (templateId: string) => {
    window.location.href = `/events/${eventSlug}/messaging/whatsapp_blasts/new?template_id=${templateId}`
  }

  const tabLabels: Record<string, string> = {
    MARKETING: 'Marketing',
    UTILITY: 'Utility',
    AUTHENTICATION: 'Authentication',
  }

  return (
    <div className="px-14 py-6">
      {templatesError && (
        <div className="mb-4 text-sm text-red-500">{templatesError}</div>
      )}

      {/* Action buttons + Search */}
      <div className="flex items-center gap-3 mb-5">
        <Input
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder="Search templates by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          size="large"
          className="flex-1"
        />
        <button
          type="button"
          onClick={handleRefetch}
          disabled={isRefetching}
          className="h-10 px-4 bg-white rounded-sm border border-[#D9D9D9] shadow-[0px_2px_0px_rgba(0,0,0,0.02)] flex items-center gap-2.5 text-sm text-black/85 leading-[22px] hover:border-[#40a9ff] hover:text-[#40a9ff] disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          <SyncOutlined spin={isRefetching} className="text-black/65" />
          Refetch
        </button>
        <Button variant="secondary" icon={metaIcon} onClick={handleOpenMetaDashboard} size="small">
          Meta Business Dashboard
        </Button>
      </div>

      {/* Category tabs */}
      {tabOrder.length > 1 && (
        <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
          {tabOrder.map((cat) => {
            const label = tabLabels[cat] || cat.charAt(0) + cat.slice(1).toLowerCase()
            const count = categoryMap[cat]?.length ?? 0
            const isActive = activeTab === cat

            return (
              <button
                key={cat}
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(cat)}
              >
                {label}
                <span className="ml-1.5 text-xs text-gray-400">({count})</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Template grid */}
      {filteredTemplates.length === 0 ? (
        <Empty
          description={
            <span className="text-sm text-gray-500">
              {templates.length === 0
                ? 'No templates available. Create templates in your Meta Business Dashboard.'
                : 'No templates found matching your search.'}
            </span>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <CompactTemplateCard
              key={template.id}
              template={template}
              onSelect={handleSelectTemplate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Configure Campaign
// ---------------------------------------------------------------------------

function ConfigureCampaignStep({
  event,
  template,
  initialCampaign,
  availableRecipients = [],
  initialRecipientFilters,
  filterOptions = EMPTY_FILTER_OPTIONS,
  mergeTagOptions,
  errors,
}: {
  event: Event
  template: WhatsappTemplate
  initialCampaign: WhatsappCampaignFormData
  availableRecipients?: Recipient[]
  initialRecipientFilters?: RecipientFilters
  filterOptions?: RecipientFilterOptions
  mergeTagOptions: MergeTagOptions
  errors?: Record<string, string | string[]>
}) {
  const {
    state,
    updateState,
    templateBody,
    allSlots,
    missingSlots,
    selectOptions,
    previewText,
    buildParameterValuesPayload,
  } = useWizardState({
    template,
    initialCampaign,
    initialRecipientFilters,
    mergeTagOptions,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const {
    parameterValues,
    headerImageType,
    headerImageUrl,
    customImageMode,
    isUploading,
    uploadPreviewUrl,
    headerTextValue,
    selectedRecipientIds,
    recipientFilters,
  } = state

  const handleImageUpload = async (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('File must be a PNG, JPEG, or WebP image')
      return false
    }
    if (file.size / 1024 / 1024 >= MAX_HEADER_IMAGE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_HEADER_IMAGE_SIZE_MB}MB`)
      return false
    }

    updateState({ isUploading: true })
    try {
      const formData = new FormData()
      formData.append('file', file)
      const data = await api.post<{ success: boolean; url: string }>(
        `/events/${event.slug}/messaging/whatsapp_blasts/upload_header_image`,
        formData,
      )
      updateState({
        headerImageUrl: data.url,
        uploadPreviewUrl: URL.createObjectURL(file),
        isUploading: false,
      })
      message.success('Image uploaded successfully')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to upload image',
      )
      updateState({ isUploading: false })
    }
    return false
  }

  const handleRemoveUploadedImage = () => {
    updateState({ headerImageUrl: '', uploadPreviewUrl: null })
  }

  const handleApplyRecipients: RecipientSelectionDrawerProps['onApply'] = ({
    recipientIds,
    filters,
  }) => {
    updateState({
      selectedRecipientIds: recipientIds,
      recipientFilters: filters,
    })
    setDrawerOpen(false)
  }

  const handleClearRecipients = () => {
    updateState({
      selectedRecipientIds: [],
      recipientFilters: defaultRecipientFilters,
    })
  }

  const getRecipientLabel = () => {
    if (recipientFilters.allAttendees) return 'All Attendees'
    if (selectedRecipientIds.length > 0) {
      return `${selectedRecipientIds.length} recipient${selectedRecipientIds.length > 1 ? 's' : ''} selected`
    }
    return null
  }

  const recipientLabel = getRecipientLabel()

  const normalizeError = (err?: string | string[]) =>
    Array.isArray(err) ? err.join(', ') : err

  const handleCancel = () => {
    router.visit(`/events/${event.slug}/messaging/whatsapp_blasts/new`)
  }

  const handleSubmit = () => {
    if (!state.campaignName.trim()) {
      message.error('Please enter a campaign name')
      return
    }

    if (
      state.selectedRecipientIds.length === 0 &&
      !state.recipientFilters.allAttendees
    ) {
      message.error('Please select at least one recipient')
      return
    }

    // Meta matches body parameters by position and rejects the whole send when one
    // is missing, so an unmapped slot is caught here rather than at delivery.
    if (missingSlots.length > 0) {
      message.error(
        `Map every variable first: ${missingSlots.map((slot) => `{{${slot}}}`).join(', ')}. ` +
          'Pick "Leave blank" for any the event has no value for.',
      )
      return
    }

    setIsSubmitting(true)

    const finalData = {
      whatsapp_campaign: {
        campaign_name: state.campaignName,
        parameter_values: buildParameterValuesPayload(),
        recipient_ids: state.selectedRecipientIds,
        recipient_filters: state.recipientFilters,
        status: initialCampaign.status || 'draft',
      },
    }

    router.post(
      `/events/${event.slug}/messaging/whatsapp_blasts?template_id=${initialCampaign.metaTemplateId}`,
      finalData,
      {
        preserveScroll: true,
        onSuccess: () => {
          message.success('Campaign created')
          router.visit(`/events/${event.slug}/messaging/whatsapp_blasts?tab=custom`, {
            preserveScroll: true,
          })
        },
        onError: (formErrors) => {
          const firstError = formErrors && Object.values(formErrors)[0]
          const normalizedError = Array.isArray(firstError)
            ? firstError[0]
            : firstError
          message.error(normalizedError || 'Failed to create campaign.')
          setIsSubmitting(false)
        },
        onFinish: () => setIsSubmitting(false),
      },
    )
  }

  return (
    <>
      <div className="flex gap-6 px-14 py-6 pb-28">
        {/* Left panel - Form */}
        <div className="w-[440px] flex-shrink-0 space-y-4">
          {/* Campaign name */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="Enter campaign name"
              value={state.campaignName}
              onChange={(e) => updateState({ campaignName: e.target.value })}
              status={errors?.campaign_name ? 'error' : undefined}
            />
            {errors?.campaign_name && (
              <div className="mt-1 text-xs text-red-500">
                {normalizeError(errors.campaign_name)}
              </div>
            )}
          </div>

          {/* Header Image Mapping */}
          {template.hasHeaderImage && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <PictureOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Image</span>
              </div>

              <Select<HeaderImageType>
                className="w-full"
                value={headerImageType}
                onChange={(val) => {
                  updateState({ headerImageType: val })
                  if (val === 'attendee.qr_code') updateState({ headerImageUrl: '' })
                }}
                options={[
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <QrcodeOutlined /> Attendee QR Code
                      </span>
                    ),
                    value: 'attendee.qr_code',
                  },
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <LinkOutlined /> Custom Image URL
                      </span>
                    ),
                    value: 'custom_url',
                  },
                ]}
              />

              {headerImageType === 'custom_url' && (
                <div className="mt-3 space-y-2">
                  <Segmented
                    block
                    size="small"
                    value={customImageMode}
                    onChange={(val) => {
                      const mode = val as CustomImageMode
                      updateState({ customImageMode: mode })
                      if (mode === 'upload')
                        updateState({
                          headerImageUrl: uploadPreviewUrl ? headerImageUrl : '',
                        })
                    }}
                    options={[
                      { label: 'Upload File', value: 'upload', icon: <UploadOutlined /> },
                      { label: 'Paste URL', value: 'url', icon: <LinkOutlined /> },
                    ]}
                  />

                  {customImageMode === 'url' ? (
                    <Input
                      placeholder="https://example.com/image.png"
                      value={headerImageUrl}
                      onChange={(e) => updateState({ headerImageUrl: e.target.value })}
                      allowClear
                    />
                  ) : uploadPreviewUrl ||
                    (headerImageUrl && !headerImageUrl.startsWith('attendee.')) ? (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={uploadPreviewUrl || headerImageUrl}
                        alt="Header preview"
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveUploadedImage}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                      >
                        <DeleteOutlined className="text-red-500 text-xs" />
                      </button>
                    </div>
                  ) : (
                    <Upload.Dragger
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      accept=".png,.jpg,.jpeg,.webp"
                      disabled={isUploading}
                      className="!border-gray-200 !bg-gray-50/50"
                    >
                      <div className="flex flex-col items-center py-2">
                        {isUploading ? (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        ) : (
                          <>
                            <UploadOutlined className="text-xl text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">
                              Click or drag image here
                            </span>
                          </>
                        )}
                      </div>
                    </Upload.Dragger>
                  )}
                </div>
              )}

              <div className="mt-2 text-[11px] text-gray-400">
                {headerImageType === 'attendee.qr_code'
                  ? 'A unique QR code image will be generated for each attendee.'
                  : headerImageUrl.trim()
                    ? 'This image will be used for all recipients.'
                    : `Upload or paste a publicly accessible image URL. Max ${MAX_HEADER_IMAGE_SIZE_MB}MB (PNG, JPEG, WebP).`}
              </div>
            </div>
          )}

          {/* Header Text Mapping */}
          {template.hasHeaderText && template.headerText && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <FontSizeOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Text</span>
              </div>

              <div className="mb-3 rounded bg-gray-50 px-3 py-2 font-mono text-[13px] text-gray-700">
                {template.headerText}
              </div>

              {headerTextHasPlaceholder(template) ? (
                <>
                  <div className="mb-1.5 text-xs text-gray-500">
                    Map <span className="font-mono">{'{{1}}'}</span> to a field:
                  </div>
                  <Select
                    className="w-full"
                    placeholder="Select a field..."
                    value={headerTextValue || undefined}
                    onChange={(val: string) => updateState({ headerTextValue: val })}
                    options={selectOptions}
                    showSearch
                    allowClear
                    onClear={() => updateState({ headerTextValue: '' })}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        ?.toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                  />
                  <div className="mt-2 text-[11px] text-gray-400">
                    The selected value will replace the placeholder in the header.
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-gray-400">
                  This header text has no placeholders and will be sent as-is.
                </div>
              )}
            </div>
          )}

          {/* Body Variable Mapping */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-1 text-sm font-medium text-gray-700">Variable Mapping</div>
            <div className="mb-4 text-xs text-gray-400">
              Map each template placeholder to attendee or event data.
            </div>

            {allSlots.length === 0 ? (
              <div className="rounded border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                No parameters detected in this template.
              </div>
            ) : (
              <div className="space-y-3">
                {allSlots.map((slot) => (
                  <div key={slot}>
                    <div className="mb-1 flex items-center gap-2">
                      <Tag className="!m-0 !text-[11px] font-mono !bg-gray-50 !border-gray-200 !text-gray-600">
                        {`{{${slot}}}`}
                      </Tag>
                      {parameterValues[String(slot)] && (
                        <span className="text-[11px] text-gray-400">
                          &rarr;{' '}
                          {labelForMergeTagValue(
                            parameterValues[String(slot)],
                            mergeTagOptions,
                          )}
                        </span>
                      )}
                    </div>
                    <Select
                      className="w-full"
                      placeholder="Select a field..."
                      value={parameterValues[String(slot)] || undefined}
                      onChange={(val: string) => {
                        updateState({
                          parameterValues: {
                            ...parameterValues,
                            [String(slot)]: val,
                          },
                        })
                      }}
                      onClear={() => {
                        const next = { ...parameterValues }
                        delete next[String(slot)]
                        updateState({ parameterValues: next })
                      }}
                      options={selectOptions}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recipient Selection */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-700">
              <HiOutlineUserGroup className="text-lg text-gray-400" />
              Recipients
            </div>

            {recipientLabel ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-green-50 border border-green-200 px-3 py-2.5">
                  <HiOutlineUser className="text-green-600 text-base flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-green-800">
                      {recipientLabel}
                    </div>
                  </div>
                  <Tag closable onClose={handleClearRecipients} className="!m-0">
                    {recipientLabel}
                  </Tag>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => setDrawerOpen(true)}
                  className="w-full"
                  size="small"
                >
                  Change Selection
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-center">
                  <HiOutlineUserGroup className="mx-auto text-2xl text-gray-300 mb-1" />
                  <div className="text-xs text-gray-500">No recipients selected</div>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setDrawerOpen(true)}
                  className="w-full"
                  size="small"
                >
                  Select Recipients
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right panel - Preview */}
        <div className="flex-1 min-w-0 space-y-4">
          {/* Selected template info (read-only) */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-2">
              <WhatsAppOutlined className="text-[#25D366]" />
              <span className="text-sm font-medium text-gray-700">Selected Template</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-800 font-medium truncate">{template.name}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {template.category && (
                  <Tag
                    color={
                      template.category.toLowerCase() === 'utility'
                        ? 'blue'
                        : template.category.toLowerCase() === 'marketing'
                          ? 'orange'
                          : 'green'
                    }
                    className="!m-0 !text-[10px] uppercase"
                  >
                    {template.category}
                  </Tag>
                )}
                {template.language && (
                  <span className="text-xs text-gray-400">{template.language}</span>
                )}
              </div>
            </div>
          </div>

          <WhatsAppPreview
            template={template}
            previewText={previewText}
            headerImageType={headerImageType}
            headerImageUrl={headerImageUrl}
            headerTextValue={headerTextValue}
            mergeTagOptions={mergeTagOptions}
            parameterValues={parameterValues}
            allSlots={allSlots}
          />
        </div>
      </div>

      <RecipientSelectionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onApply={handleApplyRecipients}
        recipients={availableRecipients}
        selectedRecipientIds={selectedRecipientIds}
        initialFilters={recipientFilters}
        filterOptions={filterOptions}
        contactField="phone"
      />

      <FixedBottomActions
        onCancel={handleCancel}
        cancelText="Cancel"
        submitText="Create Campaign"
        submitLoading={isSubmitting}
        rightActions={
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
            Create Campaign
          </Button>
        }
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

const CreateWhatsAppCampaign = () => {
  const {
    event,
    customTemplates = [],
    templatesError,
    template,
    initialCampaign,
    availableRecipients = [],
    initialRecipientFilters,
    filterOptions = EMPTY_FILTER_OPTIONS,
    mergeTagOptions,
    errors,
  } = usePage<PageProps>().props

  const hasTemplate = !!template && !!initialCampaign && !!mergeTagOptions
  const currentStep: 0 | 1 = hasTemplate ? 1 : 0

  const handleStepClick = (step: 0 | 1) => {
    if (step === 0) {
      window.location.href = `/events/${event.slug}/messaging/whatsapp_blasts/new`
    }
  }

  const handleCancel = () => {
    router.visit(`/events/${event.slug}/messaging/whatsapp_blasts?tab=custom`)
  }

  return (
    <EventsLayout>
      <PageHeader
        title="Create WhatsApp Campaign"
        breadcrumb={[
          {
            title: 'Whatsapp Blast',
            href: `/events/${event.slug}/messaging/whatsapp_blasts`,
          },
          { title: 'Create' },
        ]}
        showBackButton
        onBack={handleCancel}
      />

      <CreationStepper currentStep={currentStep} onStepClick={handleStepClick} />

      {currentStep === 0 && (
        <TemplateSelectionStep
          templates={customTemplates}
          eventSlug={event.slug}
          templatesError={templatesError}
        />
      )}

      {currentStep === 1 && hasTemplate && (
        <ConfigureCampaignStep
          event={event}
          template={template!}
          initialCampaign={initialCampaign!}
          availableRecipients={availableRecipients}
          initialRecipientFilters={initialRecipientFilters}
          filterOptions={filterOptions}
          mergeTagOptions={mergeTagOptions!}
          errors={errors}
        />
      )}
    </EventsLayout>
  )
}

export default CreateWhatsAppCampaign
